import type {
  ComponentPage,
  ComponentType,
  FleetRow,
  Health,
  InstallResult,
  InstallTarget,
  Me,
  MyShifts,
  TeamMember,
  BoardOrder,
  BuildableRevision,
  ShortageRow,
  StockRow,
  Repository,
  ScanResult,
  Revision,
  StructureSaved,
  WorkRow,
  ExecutionPage,
  ProcedureOption,
} from '../repository'
import type { HoursReport, PeriodSummary, Serial, Shift } from '@kronos/domain'
import { HttpError, get, post } from './transport'
import { inventoryHttp } from './inventoryHttp'
import { scheduleHttp } from './scheduleHttp'
import type { Attachment } from '@kronos/domain'

/**
 * The only transport code in the app.
 *
 * Same-origin throughout — Caddy puts the API under `/api` on the app's own
 * origin in production, and Vite's proxy does the same in dev. There is no CORS
 * configuration in this repository as a result, and no difference between the
 * two environments to debug.
 */

export { HttpError } from './transport'

export function createHttpRepository(): Repository {
  return {
    ...scheduleHttp(),
    ...inventoryHttp(),
    me: () => get<Me>('/api/me'),
    signOut: async () => {
      await post<unknown>('/api/auth/logout', {})
    },
    team: () => get<readonly TeamMember[]>('/api/users'),
    setRoles: (userId, roles) =>
      post<TeamMember>(`/api/users/${encodeURIComponent(userId)}/roles`, { roles }),

    myShifts: (date) => get<MyShifts>(`/api/shifts/mine${date ? `?date=${encodeURIComponent(date)}` : ''}`),
    myHistory: () => get<readonly PeriodSummary[]>('/api/shifts/mine/history'),
    clockIn: () => post<Shift>('/api/shifts/clock-in', {}),
    clockOut: () => post<{ shift: Shift | null }>('/api/shifts/clock-out', {}),
    addShift: (input) => post<Shift>('/api/shifts', input),
    editShift: (id, input) => post<Shift>(`/api/shifts/${encodeURIComponent(id)}`, input, 'PATCH'),
    hours: (date) => get<HoursReport>(`/api/hours${date ? `?date=${encodeURIComponent(date)}` : ''}`),
    // A link rather than a fetch: the browser downloads it with the session
    // cookie and its own save dialog, which is what a PDF wants.
    hoursPdfHref: (date) => `/api/hours.pdf${date ? `?date=${encodeURIComponent(date)}` : ''}`,

    buildOrders: (includeCancelled) =>
      get<readonly BoardOrder[]>(`/api/build-orders${includeCancelled ? '?all=true' : ''}`),
    buildableRevisions: () => get<readonly BuildableRevision[]>('/api/buildable-revisions'),
    createBuildOrder: (input) => post<BoardOrder>('/api/build-orders', input),
    releaseBuildOrder: (id) =>
      post<BoardOrder>(`/api/build-orders/${encodeURIComponent(id)}/release`, {}),
    cancelBuildOrder: (id) =>
      post<BoardOrder>(`/api/build-orders/${encodeURIComponent(id)}/cancel`, {}),
    startBuildUnit: (id, serial) =>
      post<{ executionId: string; serial: string }>(
        `/api/build-orders/${encodeURIComponent(id)}/start`,
        { serial },
      ),
    shortages: () => get<readonly ShortageRow[]>('/api/shortages'),
    scan: async (serial) => {
      try {
        return await get<ScanResult>(`/api/scan/${encodeURIComponent(serial)}`)
      } catch (e) {
        // A serial nobody has heard of is an *answer* — "that label is not one
        // of ours" — and the scan loop has to keep running. Only a 404 means
        // that; anything else is a real failure and must surface.
        if (e instanceof HttpError && e.status === 404) return { kind: 'unknown' }
        throw e
      }
    },
    health: () => get<Health>('/api/health'),

    componentTypes: () => get<readonly ComponentType[]>('/api/component-types'),
    revisions: (componentTypeId) =>
      get<readonly Revision[]>(
        componentTypeId
          ? `/api/revisions?componentTypeId=${encodeURIComponent(componentTypeId)}`
          : '/api/revisions',
      ),
    component: (id) => get<ComponentPage>(`/api/components/${encodeURIComponent(id)}`),
    installTargets: (id) =>
      get<readonly InstallTarget[]>(`/api/components/${encodeURIComponent(id)}/targets`),
    fleet: () => get<readonly FleetRow[]>('/api/fleet'),
    stock: () => get<readonly StockRow[]>('/api/parts'),

    createComponentType: (input) => post<{ id: string }>('/api/component-types', input),
    updateComponentType: (id, input) =>
      post(`/api/component-types/${encodeURIComponent(id)}`, input),
    createRevision: (input) => post<{ id: string }>('/api/revisions', input),
    saveStructure: (componentTypeId, lines) =>
      post<StructureSaved>(
        `/api/component-types/${encodeURIComponent(componentTypeId)}/structure`,
        { lines },
      ),

    intake: (serial, componentTypeId, supplierSerial) =>
      post('/api/intake', { serial, componentTypeId, supplierSerial }),
    receiveQuantity: (componentTypeId, amount, lotNumber) =>
      post<{ onHand: number }>('/api/receive-quantity', { componentTypeId, amount, lotNumber }),
    receiveUnmarked: (componentTypeId, count) =>
      post<{ serials: readonly Serial[] }>('/api/intake/unmarked', { componentTypeId, count }),
    commissionRobot: (serial, fleetNumber, site) =>
      post('/api/robots', { serial, fleetNumber, site }),
    install: (childSerial, parentSerial, position, deviationReason) =>
      post<InstallResult>('/api/install', {
        childSerial,
        parentSerial,
        position,
        ...(deviationReason ? { deviationReason } : {}),
      }),
    remove: (componentSerial, reason) => post('/api/remove', { componentSerial, reason }),

    work: (includeClosed) => get<readonly WorkRow[]>(`/api/work?closed=${includeClosed ? 'true' : 'false'}`),
    execution: (id) => get<ExecutionPage>(`/api/executions/${encodeURIComponent(id)}`),
    proceduresFor: (componentId) =>
      get<readonly ProcedureOption[]>(`/api/components/${encodeURIComponent(componentId)}/procedures`),
    openExecution: (procedureId, componentId) =>
      post<{ id: string }>('/api/executions', { procedureId, componentId }),
    answerStep: (executionId, stepId, outcome, note) =>
      post(`/api/executions/${encodeURIComponent(executionId)}/steps`, { stepId, outcome, note }),
    closeExecution: (executionId) =>
      post<{ id: string }>(`/api/executions/${encodeURIComponent(executionId)}/close`, {}),

    attachments: (kind, id) =>
      get<readonly Attachment[]>(
        `/api/attachments?subject=${encodeURIComponent(kind)}&id=${encodeURIComponent(id)}`,
      ),

    /**
     * The image goes up as a **raw body**, not multipart.
     *
     * The browser has already resized it, so there is exactly one field to
     * send; a multipart encoder would be machinery bought to wrap a single
     * blob. Everything else rides in the query string, where it shows up in a
     * log when something goes wrong.
     */
    uploadAttachment: async (subject, blob, meta) => {
      const params = new URLSearchParams({
        subject: subject.kind,
        id: subject.id,
        width: String(meta.width),
        height: String(meta.height),
      })
      if (meta.caption) params.set('caption', meta.caption)
      if (subject.kind === 'execution' && subject.stepId) params.set('stepId', subject.stepId)
      const response = await fetch(`/api/attachments?${params.toString()}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': blob.type || 'application/octet-stream' },
        body: blob,
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string }
        throw new HttpError(response.status, payload.error ?? response.statusText)
      }
      return (await response.json()) as Attachment
    },

    /**
     * A file, sent exactly as it sits on disk.
     *
     * Nothing is resized, re-encoded or inspected: a STEP file that arrives one
     * byte different from the one the engineer exported is not the same file,
     * and the SHA-256 the server stores is what makes that checkable later.
     *
     * The content type is forced to `application/octet-stream` rather than
     * taken from the `File`, because the browser guesses it from the extension
     * and has no entry for `.step`, `.GTL` or half of what a board house wants
     * — so `file.type` is empty often enough that the server would have no
     * parser to match. The real type rides in the query string as a label.
     */
    uploadDocument: async (subject, file, meta) => {
      const params = new URLSearchParams({
        subject: subject.kind,
        id: subject.id,
        purpose: 'document',
        filename: file.name,
      })
      if (meta.role) params.set('role', meta.role)
      if (file.type) params.set('type', file.type)
      if (subject.kind === 'execution' && subject.stepId) params.set('stepId', subject.stepId)
      const response = await fetch(`/api/attachments?${params.toString()}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/octet-stream' },
        body: file,
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string }
        throw new HttpError(response.status, payload.error ?? response.statusText)
      }
      return (await response.json()) as Attachment
    },

    deleteAttachment: async (id) => {
      const response = await fetch(`/api/attachments/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        credentials: 'same-origin',
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => ({}))) as { error?: string }
        throw new HttpError(response.status, payload.error ?? response.statusText)
      }
      return (await response.json()) as { fileRemoved: boolean }
    },

    makePrimaryAttachment: (id) => post(`/api/attachments/${encodeURIComponent(id)}`, { primary: true }),
  }
}
